/* Search: shared behaviour for the search page and the header overlay.
   - results update as you type (predictive search, section rendering); skeleton rows show the moment a query
     reaches two characters, until the first results land (later refinements just dim the current list)
   - the first result is highlighted as soon as results appear; ArrowDown / ArrowUp move through them
     (ArrowUp past the first scrolls back up to the input), Enter opens the highlighted one, Escape closes the overlay
   - the overlay focuses its input on open */
(() => {
  const overlay = document.querySelector('[data-search-overlay]');
  const motion = () => window.CJR?.motion;

  const initPanel = (root) => {
    const input = root.querySelector('[data-search-input]');
    const form = root.querySelector('[data-search-form]');
    const live = root.querySelector('[data-search-live]');
    const staticResults = root.querySelector('[data-search-static]');
    const scroller = root.closest('[data-search-overlay]') || window; // overlay scrolls itself; the page scrolls the window
    const behavior = () => (motion()?.reduced ? 'auto' : 'smooth');
    let timer, controller, active = -1;

    const results = () => {
      const scope = live.innerHTML.trim() ? live : staticResults;
      return scope ? [...scope.querySelectorAll('.search-result')] : [];
    };
    const setActive = (i, { scroll = true } = {}) => {
      const items = results();
      items.forEach((el) => el.classList.remove('is-active'));
      active = items.length ? Math.max(-1, Math.min(i, items.length - 1)) : -1;
      if (active >= 0) {
        items[active].classList.add('is-active');
        if (scroll) items[active].scrollIntoView({ block: 'nearest', behavior: behavior() });
        input.setAttribute('aria-activedescendant', items[active].id || '');
      } else {
        input.removeAttribute('aria-activedescendant');
        if (scroll) scroller.scrollTo({ top: 0, behavior: behavior() });
      }
    };

    // placeholder rows in the result grid, with title bars of varying length so they read as a list
    const skeleton = () => `<ul class="search-skeleton" aria-hidden="true">${[58, 42, 66, 50, 36].map((w) =>
      `<li class="search-skeleton__row"><span class="search-result__media"></span><span class="search-skeleton__bar" style="width:${w}%"></span><span class="search-skeleton__bar search-skeleton__bar--price"></span></li>`).join('')}</ul>`;
    const query = () => input.value.trim();

    const run = async () => {
      const q = query();
      controller?.abort();
      if (q.length < 2) { live.innerHTML = ''; setActive(-1); input.setAttribute('aria-expanded', 'false'); return; }
      controller = new AbortController();
      root.classList.add('is-loading');
      live.setAttribute('aria-busy', 'true');
      try {
        const url = `${root.dataset.suggestUrl}?q=${encodeURIComponent(q)}&resources[type]=product,article,page&resources[limit]=10&resources[options][unavailable_products]=last&section_id=predictive-search`;
        const html = await fetch(url, { signal: controller.signal }).then((r) => r.text());
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const section = doc.querySelector('.shopify-section');
        const oldList = live.querySelector('.search__results');
        const newList = section?.querySelector('.search__results');
        if (oldList && newList) {
          // refining a query: rows still in the results are kept as they are (no repaint, no image blink), new ones slot in between.
          // Matched on path: the urls also carry per-query tracking params (_psq, _pos…), which the kept row takes over.
          const path = (li) => li.dataset.url.split('?')[0];
          const kept = new Map([...oldList.children].map((li) => [path(li), li]));
          oldList.replaceChildren(...[...newList.children].map((li) => {
            const old = kept.get(path(li));
            if (!old) return li;
            old.dataset.url = li.dataset.url;
            const hrefs = [...li.querySelectorAll('a[href]')];
            old.querySelectorAll('a[href]').forEach((a, k) => { if (hrefs[k]) a.href = hrefs[k].getAttribute('href'); });
            return old;
          }));
        } else {
          const refreshing = !!live.querySelector('.search__results, .search__empty'); // something is already showing: swap in place, no fade
          live.innerHTML = section?.innerHTML || '';
          live.classList.toggle('is-refresh', refreshing);
        }
        live.querySelectorAll('.search-result').forEach((el, i) => { el.id = `${root.id || 'search'}-result-${i}`; });
        input.setAttribute('aria-expanded', 'true');
        setActive(0, { scroll: false });
        if (!root.closest('[data-search-overlay]')) {
          const next = new URL(window.location); next.searchParams.set('q', q); history.replaceState({}, '', next);
        }
      } catch (e) {
        if (e.name !== 'AbortError') live.innerHTML = '';
      } finally {
        root.classList.remove('is-loading');
        live.removeAttribute('aria-busy');
      }
    };

    input.addEventListener('input', () => {
      clearTimeout(timer);
      const q = query();
      if (q.length < 2) live.innerHTML = ''; // back under two characters: nothing to show (static results return on the page)
      else if (!live.querySelector('.search-result, .search-skeleton')) live.innerHTML = skeleton(); // no results yet: show the fetch is under way (once; later letters don't restart it)
      timer = setTimeout(run, 180);
    });
    input.addEventListener('keydown', (e) => {
      const items = results();
      if (e.key === 'ArrowDown') { e.preventDefault(); setActive(active + 1); }
      else if (e.key === 'ArrowUp') { e.preventDefault(); setActive(active - 1); }
      else if (e.key === 'Enter') {
        if (active >= 0 && items[active]) { e.preventDefault(); window.location.assign(items[active].dataset.url); }
        // otherwise the form submits to the search page
      } else if (e.key === 'Escape' && overlay && root.closest('[data-search-overlay]')) { e.preventDefault(); close(); }
    });
    // hovering a result highlights it, clicking anywhere on the row opens it
    const scopes = [live, staticResults].filter(Boolean);
    scopes.forEach((scope) => {
      scope.addEventListener('mousemove', (e) => { const li = e.target.closest('.search-result'); if (li) setActive(results().indexOf(li), { scroll: false }); });
      scope.addEventListener('click', (e) => { const li = e.target.closest('.search-result'); if (li && !e.target.closest('a')) window.location.assign(li.dataset.url); });
    });
    staticResults?.querySelectorAll('.search-result').forEach((el, i) => { el.id = `${root.id || 'search'}-static-${i}`; });
    if (staticResults) setActive(0, { scroll: false });
    return { input, live, reset: () => { input.value = ''; live.innerHTML = ''; setActive(-1, { scroll: false }); } };
  };

  const panels = [...document.querySelectorAll('[data-search]')].map(initPanel);
  const overlayPanel = overlay ? panels.find((p) => overlay.contains(p.input)) : null;

  const open = () => {
    if (!overlay || !overlay.hidden) return;
    overlay.hidden = false;
    document.documentElement.classList.add('search-open');
    const panel = overlay.querySelector('.search');
    if (motion() && typeof anime === 'function' && !motion().reduced) {
      anime.remove([overlay, panel]);
      anime({ targets: overlay, opacity: [0, 1], duration: 220, easing: 'easeOutQuad' });
      anime({ targets: panel, translateY: [12, 0], opacity: [0, 1], easing: motion().SPRING_SNAPPY });
    }
    overlayPanel.input.focus();
  };
  const close = async () => {
    if (!overlay || overlay.hidden) return;
    if (motion() && typeof anime === 'function' && !motion().reduced) {
      anime.remove(overlay);
      await anime({ targets: overlay, opacity: 0, duration: 160, easing: 'easeInQuad' }).finished;
    }
    overlay.hidden = true;
    overlay.style.opacity = '';
    document.documentElement.classList.remove('search-open');
    overlayPanel.reset();
    document.querySelector('[data-search-open]')?.focus();
  };

  document.addEventListener('click', (e) => {
    const trigger = e.target.closest('[data-search-open]');
    if (trigger && overlay) { e.preventDefault(); overlay.hidden ? open() : close(); return; }
    if (e.target.closest('[data-search-close]')) close();
  });
  document.addEventListener('keydown', (e) => {
    if (e.key === 'Escape' && overlay && !overlay.hidden) close();
    // "/" opens search when not typing elsewhere
    if (e.key === '/' && overlay && overlay.hidden && !/^(INPUT|TEXTAREA)$/.test(document.activeElement?.tagName || '')) { e.preventDefault(); open(); }
  });
  window.CJR = window.CJR || {}; window.CJR.search = { open, close };
})();
